import { ActionType } from "game-common/character/character"
import { LpcSheet } from "game-common/lpc/lpc"
import { Coordinate, Entity, IdAware, IndexedMap, randomIntBetween } from "game-common/models"
import { Callback, degrees_to_radians, radians_to_cardinal_dir, randomId } from "game-common/util"
import {
    AnimatedSprite,
    Application,
    BitmapText,
    Container,
    Graphics,
    LoaderResource,
    Matrix,
    Rectangle,
    Sprite,
    Texture,
    Ticker,
} from "pixi.js"

import { SpriteMeta } from "../entity_rendering_manager"
import { BaseRenderable } from "./base_renderable"
import { EntityHighlight, HighlightFrame } from "./entity_highlight"
import { HALF_FRAME, LpcJobResult, LpcRenderProps, lpcCompositeGenerator } from "./lpc_composite_generator"
import { LoadingLifecycle, spriteSheetLoader } from "./spritesheet_loader"
import { logger } from "../../../../logger"
import { Mechanics } from "game-common/mechanics/mechanics"
import { shatterSprite } from "../effects/sprite_shatter"
import { PlayerRing } from "./player_ring"

export const AnimationDictionaryTypes = [
    "walk-left",
    "walk-right",
    "walk-back",
    "walk-front",
    "idle-left",
    "idle-right",
    "idle-back",
    "idle-front",
    "thrust-left",
    "thrust-right",
    "thrust-back",
    "thrust-front",
    "slash-left",
    "slash-right",
    "slash-back",
    "slash-front",
    "shoot-left",
    "shoot-right",
    "shoot-back",
    "shoot-front",
    "hurt-front",
    "activate",
    "deactivate",
    "inactive",
    "active",
] as const

const AnimationTypeToActionType: Record<AnimationDictionaryType, ActionType> = {
    activate: "activate",
    deactivate: "deactivate",
    inactive: "inactive",
    active: "activate",
    "walk-left": "walk",
    "walk-right": "walk",
    "walk-back": "walk",
    "walk-front": "walk",
    "idle-left": "idle",
    "idle-right": "idle",
    "idle-back": "idle",
    "idle-front": "idle",
    "thrust-left": "thrust",
    "thrust-right": "thrust",
    "thrust-back": "thrust",
    "thrust-front": "thrust",
    "slash-left": "slash",
    "slash-right": "slash",
    "slash-back": "slash",
    "slash-front": "slash",
    "shoot-left": "shoot",
    "shoot-right": "shoot",
    "shoot-back": "shoot",
    "shoot-front": "shoot",
    "hurt-front": "hurt",
}

export type AnimationDictionaryType = (typeof AnimationDictionaryTypes)[number]

export type LpcAnimationRenderMeta = Record<AnimationDictionaryType, SpriteMeta>

// Defined alongside the generator, whose cache key it feeds; re-exported here because this is
// where callers have always imported it from.
export type { LpcRenderProps }

export interface LpcSpriteRenderMeta {
    alwaysShowLabel?: boolean
    disallowMoveAttackAnimation?: boolean
    renderMeta?: Partial<LpcAnimationRenderMeta>
    attackAction?: ActionType
    angle?: number
    descriptors: LpcSheet[]
    hasHalo?: boolean
    isSubmerged?: boolean
    hasShadow?: boolean
    // Marks this body as being driven by another real player (see PlayerRing). Never set for the
    // client's own character -- you already know where you are.
    hasPlayerRing?: boolean
    id?: string
    priority?: boolean
    loadObserver?: LoadObserver
    defaultAction?: ActionType
}

// Where the soles sit INSIDE a baked composite frame.
//
// The body is always a 64px LPC frame centred in whatever the composite baked to, so its feet are
// a FIXED distance below the frame's centre -- they do not scale with the frame. An oversize slash
// bakes at 192x192 with that same 64px body in the middle, so treating the feet as a proportion of
// the frame height (`fh * feetY / 64`) drops them 56px too low for the length of the swing, which
// is what put the projected shadow well below the character's feet mid-attack.
//
// Paired with the generator's OVERSIZE_OFFSET: that shifts the oversize frame so the body lands
// where the 64px frames put it, and this puts the sole line back at the same place inside it, so
// `animation.y + soleLineInFrame(...)` is the same ground line for every action.
export const soleLineInFrame = (frameHeight: number, feetY: number): number =>
    frameHeight / 2 + (feetY - HALF_FRAME)

const defaultRenderMeta: LpcAnimationRenderMeta = {
    activate: {
        scale: 1,
        speed: 0.25,
    },
    deactivate: {
        scale: 1,
        speed: 0.25,
    },
    inactive: {
        scale: 1,
        animated: false,
    },
    active: {
        scale: 1,
    },
    "walk-left": {
        scale: 1,
        speed: 0.25,
    },
    "walk-right": {
        scale: 1,
        speed: 0.25,
    },
    "walk-back": {
        scale: 1,
        speed: 0.25,
    },
    "walk-front": {
        scale: 1,
        speed: 0.25,
    },
    "idle-left": {
        scale: 1,
    },
    "idle-right": {
        scale: 1,
    },
    "idle-back": {
        scale: 1,
    },
    "idle-front": {
        scale: 1,
    },
    "thrust-left": {
        scale: 1,
        speed: 0.55,
    },
    "thrust-right": {
        scale: 1,
        speed: 0.55,
    },
    "thrust-back": {
        scale: 1,
        speed: 0.55,
    },
    "thrust-front": {
        scale: 1,
        speed: 0.55,
    },
    "slash-left": {
        scale: 1,
        speed: 0.25,
    },
    "slash-right": {
        scale: 1,
        speed: 0.25,
    },
    "slash-back": {
        scale: 1,
        speed: 0.25,
    },
    "slash-front": {
        scale: 1,
        speed: 0.25,
    },
    "shoot-left": {
        scale: 1,
        speed: 0.85,
    },
    "shoot-right": {
        scale: 1,
        speed: 0.85,
    },
    "shoot-back": {
        scale: 1,
        speed: 0.85,
    },
    "shoot-front": {
        scale: 1,
        speed: 0.85,
    },
    "hurt-front": {
        scale: 1,
    },
}

const animationToAction = (animation: AnimationDictionaryType): ActionType => animation.split("-")[0] as ActionType

const isDirectional = (type: AnimationDictionaryType) => type.includes("-")

const isAttackAnimation = (animationName: AnimationDictionaryType) =>
    ["shoot", "thrust", "slash"].includes(animationToAction(animationName))

const isActivationAnimation = (animationName: AnimationDictionaryType) =>
    ["activate", "deactivate"].includes(animationToAction(animationName))

const isWalkAnimation = (animationName: AnimationDictionaryType) => ["walk"].includes(animationToAction(animationName))

const extractDirection = (animation: AnimationDictionaryType) => animation.split("-")[1]

type AnimationLoadingState = LoadingLifecycle | "failed"

interface ActionListener extends IdAware {
    callback: Callback<void>
    callbackId?: string
}

interface CallbackState {
    entityId: string
    callback: Callback<void>
    animationName: string
    actionType: ActionType
    createdAt: number
    status: "pending" | "executed" | "cancelled"
}

const actionTypeToAnimationAction = (actionType: ActionType): AnimationDictionaryType => {
    if (actionType === "activate") {
        return "activate"
    }
    if (actionType === "deactivate") {
        return "deactivate"
    }
    if (actionType === "inactive") {
        return "inactive"
    }
    if (actionType === "active") {
        return "active"
    }
}

const isLooped = (animation: AnimationDictionaryType) => {
    if (isAttackAnimation(animation) || isActivationAnimation(animation)) {
        return false
    }

    return true
}

export class LoadObserver {
    doneCallback: Callback<void>
    jobs: Set<string> = new Set()
    completed: boolean

    constructor(doneCallback: Callback<void>) {
        this.doneCallback = doneCallback
    }

    offer = (job: string) => {
        this.jobs.add(job)
    }

    complete = (job: string) => {
        this.jobs.delete(job)
        if (this.completed) {
            return
        }

        if (this.jobs.size < 1) {
            this.completed = true
            this.doneCallback()
        }
    }

    clear = () => {
        this.jobs.clear()
        this.completed = false
    }
}

// A sprite that participates in periodic callback maintenance. The ticker only needs these two
// operations, so it stays decoupled from the full LpcCompositeSprite type.
interface PeriodicCallbackMaintenance {
    checkForMissingCallbacks(): void
    cleanupOrphanedCallbacks(): void
}

// Single shared driver for every LpcCompositeSprite's periodic callback maintenance. Each sprite
// used to create its OWN setInterval(100ms) + setInterval(10s); with many sprites on screen that
// was dozens of independent timer wake-ups per second on the main thread -- a real cost on low-end
// CPUs. One shared pair of timers iterating the registered set is far cheaper, and each sprite's
// check early-returns when it has no active callbacks. The timers exist only while >=1 sprite is
// registered, so an empty scene schedules nothing.
class LpcSpriteMaintenanceTicker {
    private sprites: Set<PeriodicCallbackMaintenance> = new Set()
    private missingTimer: any
    private orphanedTimer: any

    register = (sprite: PeriodicCallbackMaintenance) => {
        this.sprites.add(sprite)
        if (this.missingTimer === undefined) {
            this.missingTimer = setInterval(this.runMissingChecks, 100)
        }
        if (this.orphanedTimer === undefined) {
            this.orphanedTimer = setInterval(this.runOrphanedCleanup, 10000)
        }
    }

    unregister = (sprite: PeriodicCallbackMaintenance) => {
        if (!this.sprites.delete(sprite)) {
            return
        }
        if (this.sprites.size === 0) {
            clearInterval(this.missingTimer)
            clearInterval(this.orphanedTimer)
            this.missingTimer = undefined
            this.orphanedTimer = undefined
        }
    }

    // Iterate a snapshot and isolate each sprite so one throwing check can't stop maintenance for
    // the rest (and can't kill the shared interval).
    private runMissingChecks = () => {
        Array.from(this.sprites).forEach(s => {
            try {
                s.checkForMissingCallbacks()
            } catch (e) {
                // best-effort maintenance
            }
        })
    }

    private runOrphanedCleanup = () => {
        Array.from(this.sprites).forEach(s => {
            try {
                s.cleanupOrphanedCallbacks()
            } catch (e) {
                // best-effort maintenance
            }
        })
    }
}

const lpcSpriteMaintenanceTicker = new LpcSpriteMaintenanceTicker()

export class LpcCompositeSprite extends BaseRenderable {
    spriteAngle: number = 0

    currentColor: number
    labelText: BitmapText

    container: Container

    lastX: number
    lastY: number

    action: AnimationDictionaryType = "idle-front"
    prevAction: AnimationDictionaryType = "idle-front"

    animations: Record<AnimationDictionaryType, LpcJobResult> = {} as Record<AnimationDictionaryType, LpcJobResult>

    // Set once this sprite has been discarded, so a late async generate callback disposes its
    // clone instead of adopting it (and leaking a cache reference).
    private spriteReleased = false
    // Cache key retained at request time for each action we've prepared (see doPreparation). Released
    // once per entry in cleanup() -- covers adopted, still-loading, and failed actions alike.
    private preparedActionKeys = new Map<AnimationDictionaryType, string>()

    idleTimeout: any

    disallowMoveAttackAnimation: boolean
    renderMeta: LpcAnimationRenderMeta
    renderProps: LpcRenderProps
    spriteRootName: string

    facingDir: string = "front"
    descriptors: LpcSheet[]

    spritesheetResource: LoaderResource

    attackAction: ActionType = "idle"

    animationDictionaryTypeLoadingState: Map<AnimationDictionaryType, AnimationLoadingState> = new Map()
    app: Application
    initialized: boolean
    identifier: string

    creationTs: number
    halo: Graphics
    shadow: Graphics
    // The "another player is here" ground marker, present only on remote player bodies.
    playerRing?: PlayerRing
    // Projected silhouette shadow (default on; window.spriteShadow = false → foot ellipse). Shares the
    // active animation's baked frame textures -- no extra bake/VRAM. Updated each frame on Ticker.shared.
    spriteShadow?: Sprite
    private feetY: number = 60
    private spriteShadowTicking = false
    // Hover outline (see setHighlighted). Created on first hover; only the pointed-at entity has one.
    private highlight?: EntityHighlight
    id: string
    priority: boolean

    actionCompleteListeners: Map<ActionType, IndexedMap<ActionListener>> = new Map()

    // Enhanced callback management
    private callbackStates = new Map<string, CallbackState>()
    private activeCallbacks = new Set<string>()
    private currentAnimationCallbacks = new Set<string>()

    // Whether this sprite is registered with the shared maintenance ticker (see startCleanupTimer).
    private registeredWithTicker: boolean = false

    loadObserver?: LoadObserver

    defaultAction?: ActionType

    constructor({
        descriptors,
        renderMeta = defaultRenderMeta,
        attackAction,
        angle,
        hasHalo,
        hasShadow,
        hasPlayerRing,
        id,
        priority,
        loadObserver,
        isSubmerged,
        defaultAction,
    }: LpcSpriteRenderMeta) {
        super()

        // TEST: Log immediately to verify logging is working
        logger.info(
            { tags: ["callback", "animation"] },
            `LPC Composite Sprite created for entity ${id}, action: ${this.action}`,
        )

        this.id = id
        this.priority = priority

        this.renderMeta = { ...defaultRenderMeta, ...renderMeta }
        this.renderProps = {
            isSubmerged,
        }
        this.attackAction = attackAction

        this.spriteAngle = angle
        this.facingDir = this.calcDir(angle)

        this.container = new Graphics()
        this.addChild(this.container)
        this.loadObserver = loadObserver

        if (hasHalo) {
            const halo = new Graphics()
            halo.alpha = 0.5
            halo.beginFill(0xffffff)
            halo.drawCircle(32, 25, 13)
            halo.endFill()
            this.halo = halo
            this.container.addChild(halo)
        }

        // The ground line this body stands on, shared by the foot shadow and the player ring. A
        // submerged body sits higher in its frame, so its feet are higher too.
        this.feetY = isSubmerged ? 55 : 60

        if (hasShadow) {
            const shadow = new Graphics()
            shadow.alpha = 0.2
            shadow.beginFill(0x000000)
            shadow.drawEllipse(32, this.feetY, 16, 8)
            shadow.endFill()
            this.shadow = shadow
            this.container.addChild(shadow)

            // Projected silhouette shadow -- on by default (window.spriteShadow = false falls back to
            // the ellipse). Sits behind the character; the per-frame update swaps which one is visible.
            const spriteShadow = new Sprite(Texture.EMPTY)
            spriteShadow.tint = 0x000000
            spriteShadow.visible = false
            this.spriteShadow = spriteShadow
            this.container.addChild(spriteShadow)

            Ticker.shared.add(this.updateSpriteShadow)
            this.spriteShadowTicking = true
        }

        if (hasPlayerRing) {
            // Added after both shadows so neither darkens the marker, and before the animation layers
            // (which arrive later, see doPreparation) so the body occludes its middle and the character
            // reads as standing inside the ring rather than in front of a decal.
            const ring = new PlayerRing()
            ring.x = 32
            ring.y = this.feetY
            this.playerRing = ring
            this.container.addChild(ring)
        }

        this.descriptors = descriptors
        this.creationTs = Date.now()
        this.defaultAction = defaultAction
    }

    // Per-frame: draw the active animation's current frame a second time, projected onto the ground
    // along the sun vector, as a real pose-accurate shadow. Reuses the entity's own baked frame textures
    // (shared, refcounted by the generator) -- no new bake, no filter. The projection is a planar shadow
    // matrix: every pixel of the sprite's height above the feet is displaced by (dirX, dirY) on the
    // ground, so (dirX, dirY) IS the sun angle. Defaults cast a shallow shadow toward the lower-left to
    // match the map's baked bush/tree shadows. window.spriteShadow = false falls back to the ellipse;
    // window.spriteShadow{DirX,DirY,Alpha} tune it live without a reload.
    updateSpriteShadow = () => {
        const shadow = this.spriteShadow
        if (!shadow) {
            return
        }

        // On by default; set window.spriteShadow = false to fall back to the foot ellipse.
        const w = window as any
        const on = w.spriteShadow !== false

        // Swap which shadow style is visible.
        if (this.shadow) {
            this.shadow.visible = !on
        }
        if (!on) {
            if (shadow.visible) {
                shadow.visible = false
                shadow.texture = Texture.EMPTY
            }
            return
        }

        const active = this.animations[this.action]?.animation
        if (!active || !active.visible || active.textures.length === 0) {
            shadow.visible = false
            return
        }
        const tex = active.textures[active.currentFrame] as Texture
        if (!tex || !tex.valid) {
            shadow.visible = false
            return
        }

        // Sun vector: ground displacement per pixel of sprite height. dirX < 0 leans left, dirY > 0
        // stretches "south". A shallow lower-left cast (|dirX| > dirY) matches the baked bush/tree art.
        const dirX = typeof w.spriteShadowDirX === "number" ? w.spriteShadowDirX : -0.23
        const dirY = typeof w.spriteShadowDirY === "number" ? w.spriteShadowDirY : 0.13
        const alpha = typeof w.spriteShadowAlpha === "number" ? w.spriteShadowAlpha : 0.3

        const fh = tex.orig?.height ?? 64
        const feet = soleLineInFrame(fh, this.feetY)
        const sx = active.scale.x
        const sy = active.scale.y

        shadow.texture = tex
        shadow.anchor.set(0, 0)
        shadow.alpha = alpha
        // Affine planar-projection matrix mapping texture (lx, ly) -> container space. A texel `hUp`
        // pixels above the feet lands at feet + hUp * (dirX, dirY); the feet themselves stay pinned.
        // The negative d-term flips the silhouette down onto the ground. active.x/scale already carry
        // any oversize offset, so the shadow tracks the sprite. fw/2 centering cancels out of tx.
        const a = sx
        const c = -sx * dirX
        const d = -sy * dirY
        const tx = active.x + feet * sx * dirX
        const ty = active.y + feet * sy * (1 + dirY)
        shadow.transform.setFromMatrix(new Matrix(a, 0, c, d, tx, ty))
        shadow.visible = true
    }

    // The frame EntityHighlight traces for the hover outline: the active animation's current baked
    // texture plus the transform it is drawn with, so the outline lands exactly on the pose (including
    // any oversize offset already baked into active.x/scale) and follows the animation frame by frame.
    // Null while the sheets are still loading or when this sprite has no art of its own -- the caller
    // then falls back to a footprint ring.
    highlightFrame = (): HighlightFrame | null => {
        const result = this.animations[this.action]
        const active: any = result?.animation ?? result?.sprite
        if (!active || !active.visible) {
            return null
        }
        const texture: Texture = result.fragmentBase()
        if (!texture?.valid) {
            return null
        }
        return {
            texture,
            x: active.x,
            y: active.y,
            scaleX: active.scale.x,
            scaleY: active.scale.y,
        }
    }

    setHighlighted = (on: boolean): boolean => {
        if (!this.highlight) {
            if (!on) {
                return true
            }
            this.highlight = new EntityHighlight({
                artContainer: this.container,
                frame: this.highlightFrame,
            })
        }
        this.highlight.set(on)
        return true
    }

    logme = (msg: string, m2: any = "", m3: any = "") => {
        // console.log(this.creationTs, "@@@", this.identifier, msg, m2, m3)
    }

    initalize = (id: string, angle: number) => {
        this.identifier = id

        const f = this.calcDir(angle)
        const action = actionTypeToAnimationAction(this.defaultAction) || (("idle-" + f) as any)
        let descriptorCountdown = this.descriptors.length
        this.logme("initializing sheets", id)

        const s = Date.now()
        this.loadObserver?.offer(action)

        // No sheets to wait on: the per-descriptor countdown below would never reach zero, leaving
        // the sprite permanently uninitialized (and blank). Initialize straight away instead.
        if (descriptorCountdown <= 0) {
            this.initialized = true
            this.loadObserver?.complete(action)
            this.startCleanupTimer()
            this.activateAnimation(action, true)
            return
        }

        this.descriptors.forEach((descriptor, i) => {
            const sheetId = `lpc/spritesheets/${descriptor.entityId}.json`
            const url = `assets/${sheetId}`
            this.logme("loading", url)

            spriteSheetLoader.load(url, _ => {
                descriptorCountdown -= 1
                if (descriptorCountdown <= 0) {
                    this.initialized = true
                    this.logme("@@@ all sheets loaded", action)
                    this.loadObserver?.complete(action)

                    // Start cleanup timer for orphaned callbacks
                    this.startCleanupTimer()

                    const elapsed = Date.now() - s
                    this.activateAnimation(action, true)
                }
            })
        })
    }

    recolor = (color: number) => {
        if (this.currentColor === color) {
            return
        }
        this.currentColor = color

        const source = this.animations[`${this.action}`]
        if (source) {
            source.recolor(color)
        }
    }

    calcDir(angle: number): string {
        return radians_to_cardinal_dir(angle)
    }

    doMovement(entity: Entity): void {
        const { movement } = entity
        if (!movement) {
            return
        }

        if (this.spriteAngle !== movement.angle) {
            const facingDir = this.calcDir(movement.angle)
            if (facingDir !== this.facingDir) {
                // Enhanced direction change handling
                this.changeDirection(facingDir)
            }
            this.facingDir = facingDir
        }
        this.spriteAngle = movement.angle

        const { x, y } = entity.location

        if (x !== this.lastX || y !== this.lastY) {
            if (this.idleTimeout !== undefined) {
                clearTimeout(this.idleTimeout)
                this.idleTimeout = undefined
            }

            if (this.lastX !== undefined && this.lastY !== undefined && !isAttackAnimation(this.action)) {
                this.activateAnimation(`walk-${this.facingDir}` as AnimationDictionaryType)
            }

            this.lastX = x
            this.lastY = y
        } else {
            if (this.idleTimeout === undefined) {
                this.idleTimeout = setTimeout(() => {
                    if (isAttackAnimation(this.action)) {
                        return
                    }
                    if (!Mechanics.entity.body.isStationary(entity)) {
                        this.activateAnimation(`idle-${this.facingDir}` as AnimationDictionaryType)
                    }
                }, 50)
            }
        }
    }

    postAnimation = (animation: AnimationDictionaryType): AnimationDictionaryType => {
        if (isAttackAnimation(animation)) {
            return `idle-${extractDirection(this.action)}` as AnimationDictionaryType
        }
        if (animation === "activate") {
            return "active"
        }
        if (animation === "deactivate") {
            return "inactive"
        }
    }

    doPreparation = (action: AnimationDictionaryType) => {
        this.logme("@@@ kicked off", action)

        this.animationDictionaryTypeLoadingState.set(action, "loading")

        // Retain this loadout's cache key NOW, at request time -- generation is async/time-budgeted
        // and the baked textures are shared across sprites, so retaining only on adopt (below) leaves
        // a window where eviction could destroy textures this sprite is about to display. Released
        // once per prepared action in cleanup().
        if (!this.preparedActionKeys.has(action)) {
            const key = lpcCompositeGenerator.computeKey(this.descriptors, action, this.renderProps)
            this.preparedActionKeys.set(action, key)
            lpcCompositeGenerator.retainKey(key)
        }

        lpcCompositeGenerator.generate(
            this.priority,
            action,
            this.renderMeta[action],
            this.renderProps,
            this.descriptors,
            (result: LpcJobResult) => {
                if (result) {
                    // Sprite was discarded before this async result arrived: dispose the orphan
                    // clone rather than adopting it into a detached container.
                    if (this.spriteReleased) {
                        lpcCompositeGenerator.abandon(result)
                        return
                    }

                    const animation = result.animation
                    const sprite = result.sprite

                    if (animation) {
                        this.container.addChild(animation)
                    }
                    if (sprite) {
                        this.container.addChild(sprite)
                    }

                    const postAnimation = this.postAnimation(action)

                    this.logme("caching", action)
                    if (animation) {
                        animation.loop = isLooped(action)
                        const listeners = this.actionCompleteListeners.get(animationToAction(action))
                        if (listeners?.size() > 0) {
                            const totalFrames = animation.totalFrames

                            animation.onFrameChange = (frame: number) => {
                                const pct = frame / (totalFrames - 1)

                                if (pct > 0.5) {
                                    // Enhanced callback execution with state tracking
                                    this.executeCallbacks(listeners, animationToAction(action))
                                }
                            }
                        }

                        if (postAnimation) {
                            animation.onComplete = () => {
                                this.activateAnimation(postAnimation)
                            }
                        } else {
                            // Add completion handler for cleanup
                            animation.onComplete = () => {
                                this.cleanupAnimationCallbacks(action)
                            }
                        }
                    }

                    if (sprite) {
                        const listeners = this.actionCompleteListeners.get(animationToAction(action))
                        if (listeners?.size() > 0) {
                            listeners.forEach(listener => {
                                listener.callback()
                                listeners.remove(listener.entityId)
                            })
                        }
                    }

                    this.animations[action] = result
                    // No retain here: the key was already retained at request time (doPreparation).
                    // Retain-at-request closes the async request->adopt window that retain-on-adopt left
                    // open (eviction could destroy the shared textures before this clone went live).
                    this.logme("is all prepared for", action)
                    this.animationDictionaryTypeLoadingState.set(action, "loaded")
                    this.activateAnimation(action, true)

                    if (sprite && postAnimation) {
                        this.activateAnimation(postAnimation)
                    }
                } else {
                    this.logme("! could not prepare", action)
                    this.animationDictionaryTypeLoadingState.set(action, "failed")
                }
            },
            // Lets the generator drop this job cheaply if the sprite is discarded (entity torn down
            // on a room change) before the job runs, so it never bakes a texture nobody wants.
            () => this.spriteReleased,
        )
    }

    prepare = (updatedAction: AnimationDictionaryType) => {
        this.logme("@@@ prepare", updatedAction)
        if (this.animations[updatedAction]) {
            this.logme("@@@ already exists", updatedAction)
            // already exists
            return
        }
        const loadState = this.animationDictionaryTypeLoadingState.get(updatedAction)
        if (loadState && loadState !== "loaded" && loadState !== "failed") {
            this.logme("@@@", updatedAction, "still in progress")
            // still in progress
            return
        }

        if (loadState === "failed") {
            this.logme("@@@", updatedAction, "failed")
            return
        }

        // kick it off
        this.doPreparation(updatedAction)
    }

    activateAnimation = (updatedAction: AnimationDictionaryType, force?: boolean) => {
        if (!this.initialized) {
            return
        }

        this.logme("->", updatedAction)
        if (updatedAction === this.action && !force) {
            // noop
            return
        }

        // Clean up callbacks for current animation before changing
        this.cleanupCurrentAnimationCallbacks()

        const source = this.animations[`${this.action}`]

        const toUpdate = this.animations[`${updatedAction}`]
        if (!toUpdate) {
            // const currentState = this.animationDictionaryTypeLoadingState[updatedAction]
            // if (currentState === "loading" || currentState === "failed") {
            //     return
            // }
            this.logme(updatedAction, "not present, prepping")
            this.prepare(`${updatedAction}`)
            return
        }

        if (source) {
            // Cancel callbacks before stopping animation
            this.cancelCurrentAnimationCallbacks()
            source.stop()
        }

        // Update action and start new animation
        this.action = updatedAction
        toUpdate?.play()

        // Reapply the current tint: each action's clone was built with whatever color was current
        // when it was baked, so an animation prepared before the last recolor() would otherwise
        // show its stale build-time color when activated.
        if (this.currentColor !== undefined) {
            toUpdate?.recolor(this.currentColor)
        }

        // CRITICAL FIX: Preserve listeners when changing animation directions for the same action
        this.preserveListenersForSameAction(updatedAction)

        // Set up callbacks for new animation
        this.setupNewAnimationCallbacks(updatedAction)

        // CRITICAL FIX: Ensure callbacks are set up even if animation is already loaded
        this.ensureCallbacksAreSetUp(updatedAction)
    }

    addActionListener = (action: ActionType, actionListener: ActionListener) => {
        const callbackId = randomId()
        const callbackState: CallbackState = {
            entityId: actionListener.entityId,
            callback: actionListener.callback,
            animationName: this.action,
            actionType: action,
            createdAt: Date.now(),
            status: "pending",
        }

        logger.debug(
            { tags: ["callback", "animation"] },
            `Adding action listener: ${callbackId} for action: ${action}, current animation: ${this.action}`,
        )

        this.callbackStates.set(callbackId, callbackState)
        this.activeCallbacks.add(callbackId)

        // Store in existing system for compatibility
        let listeners = this.actionCompleteListeners.get(action)
        if (!listeners) {
            listeners = new IndexedMap<ActionListener>()
            this.actionCompleteListeners.set(action, listeners)
            logger.debug({ tags: ["callback", "animation"] }, `Created new listeners map for action: ${action}`)
        }
        listeners.add({ ...actionListener, callbackId })
        logger.debug(
            { tags: ["callback", "animation"] },
            `Added listener to map. Total listeners for ${action}: ${listeners.size()}`,
        )
    }

    doAttack(entity: Entity, callback?: Callback<void>): void {
        // TEST: Log immediately to verify attack logging is working
        logger.info(
            { tags: ["callback", "animation"] },
            `ATTACK TEST: doAttack called for entity ${entity.entityId}, callback: ${!!callback}, action: ${this.action}`,
        )

        logger.debug(
            { tags: ["callback", "animation"] },
            `doAttack called with callback: ${!!callback}, current action: ${this.action}`,
        )

        // Cancel any existing attack callbacks
        this.cancelAttackCallbacks()

        if (isAttackAnimation(this.action)) {
            logger.debug({ tags: ["callback", "animation"] }, `Already attacking, replacing callback`)
            // If already attacking, replace the callback
            this.replaceAttackCallback(callback)
            return
        }

        if (this.disallowMoveAttackAnimation && isWalkAnimation(this.action)) {
            logger.debug(
                { tags: ["callback", "animation"] },
                `Attack blocked - disallowMoveAttackAnimation and walking`,
            )
            return
        }
        this.prevAction = this.action
        const newAction: AnimationDictionaryType = `${this.attackAction}-${this.facingDir}` as AnimationDictionaryType
        logger.debug({ tags: ["callback", "animation"] }, `Starting attack animation: ${newAction}`)

        if (callback) {
            logger.debug(
                { tags: ["callback", "animation"] },
                `Adding callback for action: ${animationToAction(newAction)}`,
            )
            this.addActionListener(animationToAction(newAction), { entityId: randomId(), callback })
        }
        this.activateAnimation(newAction)
    }

    fragment = (entity: Entity) => {
        let actionToUse = this.action
        if (!this.renderMeta[this.action]) {
            actionToUse = `idle-${this.facingDir}` as AnimationDictionaryType
        }
        if (!this.renderMeta[actionToUse]) {
            return
        }

        const { scale } = this.renderMeta[actionToUse]
        const surface = new Graphics()
        surface.x = this.parent?.x - this.parent.width * 0.5
        surface.y = this.parent?.y - this.parent.height * 0.5
        surface.scale.set(scale)
        surface.rotation = this.container.rotation
        this.parent.parent.addChild(surface)

        const f: Texture = this.animations[`${actionToUse}`]?.fragmentBase()
        if (!f) {
            return
        }
        shatterSprite(surface, f, { cacheKey: entity.appearance?.race })
    }

    // Enhanced callback management methods
    private cleanupCurrentAnimationCallbacks() {
        const currentAction = animationToAction(this.action)
        const listeners = this.actionCompleteListeners.get(currentAction)
        if (listeners) {
            listeners.forEach(listener => {
                const callbackId = (listener as any).callbackId
                if (callbackId) {
                    this.cancelCallback(callbackId)
                }
            })
            listeners.clear()
        }
    }

    private cancelCurrentAnimationCallbacks() {
        this.currentAnimationCallbacks.forEach(callbackId => {
            this.cancelCallback(callbackId)
        })
        this.currentAnimationCallbacks.clear()
    }

    private setupNewAnimationCallbacks(animationName: AnimationDictionaryType) {
        const action = animationToAction(animationName)
        const listeners = this.actionCompleteListeners.get(action)

        if (listeners?.size() > 0) {
            listeners.forEach(listener => {
                const callbackId = (listener as any).callbackId
                if (callbackId) {
                    this.currentAnimationCallbacks.add(callbackId)
                }
            })
        }
    }

    private cancelAttackCallbacks() {
        const attackActions = ["thrust", "slash", "shoot"]
        attackActions.forEach(action => {
            const listeners = this.actionCompleteListeners.get(action as ActionType)
            if (listeners) {
                listeners.forEach(listener => {
                    const callbackId = (listener as any).callbackId
                    if (callbackId) {
                        this.cancelCallback(callbackId)
                    }
                })
                listeners.clear()
            }
        })
    }

    private replaceAttackCallback(callback?: Callback<void>) {
        if (!callback) return

        const currentAction = animationToAction(this.action)
        const listeners = this.actionCompleteListeners.get(currentAction)
        if (listeners) {
            // Clear existing callbacks
            listeners.forEach(listener => {
                const callbackId = (listener as any).callbackId
                if (callbackId) {
                    this.cancelCallback(callbackId)
                }
            })
            listeners.clear()

            // Add new callback
            this.addActionListener(currentAction, {
                entityId: randomId(),
                callback,
            })
        }
    }

    private cancelCallback(callbackId: string) {
        const callbackState = this.callbackStates.get(callbackId)
        if (callbackState && callbackState.status === "pending") {
            callbackState.status = "cancelled"
            this.activeCallbacks.delete(callbackId)
            this.currentAnimationCallbacks.delete(callbackId)
        }
    }

    private executeCallbacks(listeners: IndexedMap<ActionListener>, action: ActionType) {
        listeners.forEach(listener => {
            const callbackId = (listener as any).callbackId

            if (callbackId && this.callbackStates.has(callbackId)) {
                const callbackState = this.callbackStates.get(callbackId)!
                if (callbackState.status === "pending") {
                    try {
                        logger.debug({ tags: ["callback", "animation"] }, `Executing callback for ${callbackId}`)
                        callbackState.callback()
                        callbackState.status = "executed"
                        logger.info(
                            { tags: ["callback", "animation"] },
                            `Callback executed successfully: ${callbackId}`,
                        )
                    } catch (error) {
                        logger.error(
                            { tags: ["callback", "animation"] },
                            `Callback execution failed for ${callbackId}:`,
                            error,
                        )
                        callbackState.status = "cancelled"
                    }
                } else {
                    logger.debug(
                        { tags: ["callback", "animation"] },
                        `Callback ${callbackId} already ${callbackState.status}`,
                    )
                }

                // Remove from active callbacks
                this.activeCallbacks.delete(callbackId)
                this.currentAnimationCallbacks.delete(callbackId)
            } else {
                logger.warn({ tags: ["callback", "animation"] }, `Callback state not found for ${callbackId}`)
            }

            listeners.remove(listener.entityId)
        })

        logger.debug({ tags: ["callback", "animation"] }, `Callback execution complete for action: ${action}`)
    }

    private cleanupAnimationCallbacks(action: AnimationDictionaryType) {
        const actionType = animationToAction(action)
        const listeners = this.actionCompleteListeners.get(actionType)
        if (listeners) {
            listeners.forEach(listener => {
                const callbackId = (listener as any).callbackId
                if (callbackId) {
                    this.cancelCallback(callbackId)
                }
            })
            listeners.clear()
        }
    }

    private changeDirection(newFacingDir: string) {
        const parts = this.action.split("-")
        const updated = `${parts[0]}-${newFacingDir}` as AnimationDictionaryType

        // Don't change direction during attack animations - let them complete
        if (isAttackAnimation(this.action)) {
            logger.debug(
                { tags: ["callback", "animation"] },
                `Preventing direction change during attack: ${this.action} -> ${updated}`,
            )
            return
        }

        // Transfer callbacks to new direction animation
        this.transferCallbacksToNewAnimation(updated)

        this.activateAnimation(updated)
    }

    private transferCallbacksToNewAnimation(newAnimation: AnimationDictionaryType) {
        const currentAction = animationToAction(this.action)
        const newAction = animationToAction(newAnimation)

        // Move callbacks from current action to new action
        const currentListeners = this.actionCompleteListeners.get(currentAction)
        if (currentListeners) {
            let newListeners = this.actionCompleteListeners.get(newAction)
            if (!newListeners) {
                newListeners = new IndexedMap<ActionListener>()
                this.actionCompleteListeners.set(newAction, newListeners)
            }

            // Transfer all listeners
            currentListeners.forEach(listener => {
                newListeners.add(listener)
            })
            currentListeners.clear()
        }
    }

    // Enhanced cleanup method
    cleanup() {
        // Same reasoning as the shadow below: drop the outline's ticker registration and its borrowed
        // texture reference before the generator reclaims the frames.
        this.highlight?.dispose()
        this.highlight = undefined

        // Leave the shared maintenance ticker. Previously each sprite created its own 100ms + 10s
        // intervals that (if never cleared) fired for the rest of the session on every sprite ever
        // built; now the sprite just deregisters and the shared timers stop once the set is empty.
        if (this.registeredWithTicker) {
            lpcSpriteMaintenanceTicker.unregister(this)
            this.registeredWithTicker = false
        }

        // Stop tracking the (about-to-be-abandoned) animation textures and drop the borrowed
        // reference before the generator reclaims them, so the shadow never points at a dead texture.
        if (this.spriteShadowTicking) {
            Ticker.shared.remove(this.updateSpriteShadow)
            this.spriteShadowTicking = false
        }
        if (this.spriteShadow) {
            this.spriteShadow.texture = Texture.EMPTY
        }

        // remove() only detaches the sprite, so the ring has to drop its live-tuning registration
        // here or a paperdoll rebuild would leave the old one registered forever.
        this.playerRing?.dispose()
        this.playerRing = undefined

        // Release every clone this sprite adopted: destroys the clone display objects and drops
        // their cache references so the generator can reclaim baked textures once no live sprite
        // uses that loadout. Guards a late generate callback from re-adopting into a dead sprite.
        this.spriteReleased = true
        // Dispose the display objects of adopted clones (refcount untouched -- keys are released just
        // below). abandon(), not release(), so we don't double-drop the key retained at request time.
        Object.keys(this.animations).forEach(action => {
            lpcCompositeGenerator.abandon(this.animations[action as AnimationDictionaryType])
        })
        this.animations = {} as Record<AnimationDictionaryType, LpcJobResult>
        // Release the request-time key hold for EVERY action we prepared (adopted, still-loading, or
        // failed) -- balances the retainKey in doPreparation exactly once per prepared action.
        this.preparedActionKeys.forEach(key => lpcCompositeGenerator.releaseKey(key))
        this.preparedActionKeys.clear()

        // Cancel all pending callbacks
        this.activeCallbacks.forEach(callbackId => {
            this.cancelCallback(callbackId)
        })

        // Clear all data structures
        this.callbackStates.clear()
        this.activeCallbacks.clear()
        this.currentAnimationCallbacks.clear()
        this.actionCompleteListeners.clear()
    }

    // Override remove so discarding a sprite (paperdoll rebuild / fade-out both call remove(),
    // not destroy()) also tears down its timers and callbacks instead of leaking them.
    remove() {
        this.cleanup()
        super.remove()
    }

    // Override destroy method
    destroy() {
        this.cleanup()
        super.destroy()
    }

    // Periodic cleanup for orphaned callbacks. Public so the shared maintenance ticker can drive it.
    cleanupOrphanedCallbacks() {
        const now = Date.now()
        const maxAge = 30000 // 30 seconds

        this.callbackStates.forEach((state, callbackId) => {
            if (state.status === "pending" && now - state.createdAt > maxAge) {
                console.warn("Cleaning up orphaned callback:", callbackId)
                this.cancelCallback(callbackId)
            }
        })
    }

    // Register for periodic callback maintenance on the shared ticker (idempotent).
    private startCleanupTimer() {
        if (this.registeredWithTicker) {
            return
        }
        this.registeredWithTicker = true
        lpcSpriteMaintenanceTicker.register(this)
    }

    // CRITICAL FIX: Ensure callbacks are set up for animations that are already loaded
    private ensureCallbacksAreSetUp(animationName: AnimationDictionaryType) {
        const action = animationToAction(animationName)
        const listeners = this.actionCompleteListeners.get(action)

        if (listeners?.size() > 0) {
            const animationResult = this.animations[animationName]
            if (animationResult && animationResult.animation && !animationResult.animation.onFrameChange) {
                logger.info(
                    { tags: ["callback", "animation"] },
                    `CRITICAL FIX: Setting up missing onFrameChange for ${animationName}`,
                )
                const animation = animationResult.animation
                const totalFrames = animation.totalFrames

                animation.onFrameChange = (frame: number) => {
                    const pct = frame / (totalFrames - 1)
                    logger.debug(
                        { tags: ["callback", "animation"] },
                        `Frame change (CRITICAL FIX): ${frame}/${totalFrames - 1} (${(pct * 100).toFixed(1)}%)`,
                    )

                    if (pct > 0.5) {
                        logger.debug(
                            { tags: ["callback", "animation"] },
                            `Triggering callbacks (CRITICAL FIX) at ${(pct * 100).toFixed(1)}% completion`,
                        )
                        this.executeCallbacks(listeners, action)
                    }
                }
            }
        }
    }

    // FALLBACK: Check for missing callbacks and set them up. Public so the shared maintenance ticker
    // can drive it; early-returns cheaply when this sprite has no active callbacks.
    checkForMissingCallbacks() {
        if (this.activeCallbacks.size === 0) {
            return // No active callbacks to check
        }

        const currentAction = animationToAction(this.action)
        const listeners = this.actionCompleteListeners.get(currentAction)

        if (listeners?.size() > 0) {
            const animationResult = this.animations[this.action]
            if (animationResult && animationResult.animation && !animationResult.animation.onFrameChange) {
                logger.info(
                    { tags: ["callback", "animation"] },
                    `FALLBACK: Detected missing onFrameChange for ${this.action}, setting up now`,
                )
                const animation = animationResult.animation
                const totalFrames = animation.totalFrames

                animation.onFrameChange = (frame: number) => {
                    const pct = frame / (totalFrames - 1)
                    logger.debug(
                        { tags: ["callback", "animation"] },
                        `Frame change (FALLBACK): ${frame}/${totalFrames - 1} (${(pct * 100).toFixed(1)}%)`,
                    )

                    if (pct > 0.5) {
                        logger.debug(
                            { tags: ["callback", "animation"] },
                            `Triggering callbacks (FALLBACK) at ${(pct * 100).toFixed(1)}% completion`,
                        )
                        this.executeCallbacks(listeners, currentAction)
                    }
                }
            }
        }
    }

    // CRITICAL FIX: Preserve listeners when changing animation directions for the same action
    private preserveListenersForSameAction(newAnimation: AnimationDictionaryType) {
        const newAction = animationToAction(newAnimation)
        const currentAction = animationToAction(this.action)

        // If we're changing to the same action, preserve listeners
        if (newAction === currentAction) {
            // Don't clear listeners for the same action
            return
        }

        // For different actions, clean up current animation callbacks
        logger.debug(
            { tags: ["callback", "animation"] },
            `Cleaning up callbacks for different action: ${currentAction} -> ${newAction}`,
        )
        this.cleanupCurrentAnimationCallbacks()
    }
}
